import type { MarketplaceSearchResult } from "@/types/api/marketplace";
import type {
  ProductSearchSuggestionsData,
  ProductSearchSuggestionsResponse,
} from "@/types/api/marketplace-product";

function formatCategoryLabel(category: string): string {
  return category
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatProductMetaLabel(
  product: ProductSearchSuggestionsData["products"][number]
): string {
  if (product.productCategories?.length) {
    return formatCategoryLabel(product.productCategories[0]);
  }
  return "Experience";
}

function formatProductLocation(
  product: ProductSearchSuggestionsData["products"][number]
): string {
  return [product.city, product.country].filter(Boolean).join(", ");
}

export function mapSearchSuggestionsToMarketplaceResults(
  data: ProductSearchSuggestionsData
): MarketplaceSearchResult[] {
  const places: MarketplaceSearchResult[] = (data.places ?? []).map(
    (place, index) => ({
      id: `place-${place.country_code}-${place.city_name || "country"}-${index}`,
      type: place.city_name ? "city" : "country",
      title: place.city_name || place.country_name,
      subtitle: place.label,
      cityName: place.city_name || undefined,
      countryCode: place.country_code,
    })
  );

  const products: MarketplaceSearchResult[] = (data.products ?? []).map(
    (product, index) => ({
      id: `product-${index}-${product.title}`,
      type: "activity",
      title: product.title,
      subtitle: formatProductLocation(product),
      metaLabel: formatProductMetaLabel(product),
      slug: product.slug,
      ...(product.slug
        ? { href: `/experiences/${product.slug}` }
        : {}),
    })
  );

  return [...places, ...products];
}

export function mapSearchSuggestionsResponse(
  response: ProductSearchSuggestionsResponse
): MarketplaceSearchResult[] {
  return mapSearchSuggestionsToMarketplaceResults({
    places: response.data.places ?? [],
    products: response.data.products ?? [],
  });
}
